import OpenAI, {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

// Cache for predictions to avoid repeated API calls
const predictionCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Retry logic with exponential backoff for rate limit errors
 */
async function retryWithBackoff(fn, maxRetries = 3, initialDelay = 1000) {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Retry on rate limit, internal server errors, and connection errors
      const shouldRetry = (
        error instanceof RateLimitError ||
        error instanceof InternalServerError ||
        error instanceof APIConnectionError
      ) && attempt < maxRetries - 1;
      
      if (shouldRetry) {
        const delay = initialDelay * Math.pow(2, attempt);
        const jitter = Math.random() * 200; // Add jitter to prevent thundering herd
        const totalDelay = delay + jitter;
        console.log(`API error (${error?.constructor?.name}), retrying in ${totalDelay?.toFixed(0)}ms (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, totalDelay));
      } else {
        // Don't retry for auth errors or max retries reached
        throw error;
      }
    }
  }
  
  throw lastError;
}

/**
 * Get cached prediction or return null if expired
 */
function getCachedPrediction(key) {
  const cached = predictionCache?.get(key);
  if (cached && Date.now() - cached?.timestamp < CACHE_DURATION) {
    return cached?.data;
  }
  return null;
}

/**
 * Store prediction in cache
 */
function setCachedPrediction(key, data) {
  predictionCache?.set(key, {
    data,
    timestamp: Date.now()
  });
}

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Historical fallback model for compliance violation predictions
 * Uses statistical analysis of past patterns when OpenAI API is unavailable
 */
function generateHistoricalCompliancePrediction(historicalData) {
  const complianceHistory = historicalData?.complianceHistory || [];
  
  if (complianceHistory?.length === 0) {
    return {
      predictions: [],
      proactive_interventions: [],
      high_risk_periods: [],
      confidence_score: 0.3,
      analysis_summary: 'Insufficient historical data for prediction. Using fallback model with limited accuracy.',
      fallback_mode: true
    };
  }

  // Analyze violation patterns
  const violationPatterns = {};
  const timePatterns = { hourly: {}, daily: {} };
  
  complianceHistory?.forEach(alert => {
    const type = alert?.alert_type || 'unknown';
    const severity = alert?.severity || 'medium';
    const date = new Date(alert?.created_at);
    const hour = date?.getHours();
    const day = date?.getDay();
    
    if (!violationPatterns?.[type]) {
      violationPatterns[type] = { count: 0, severities: {}, locations: [] };
    }
    violationPatterns[type].count++;
    violationPatterns[type].severities[severity] = (violationPatterns?.[type]?.severities?.[severity] || 0) + 1;
    if (alert?.location) violationPatterns?.[type]?.locations?.push(alert?.location);
    
    timePatterns.hourly[hour] = (timePatterns?.hourly?.[hour] || 0) + 1;
    timePatterns.daily[day] = (timePatterns?.daily?.[day] || 0) + 1;
  });

  // Generate predictions based on frequency
  const predictions = Object.entries(violationPatterns)?.sort((a, b) => b?.[1]?.count - a?.[1]?.count)?.slice(0, 5)?.map(([type, data]) => {
      const probability = Math.min(data?.count / complianceHistory?.length, 0.95);
      const mostCommonSeverity = Object.entries(data?.severities)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || 'medium';
      
      return {
        violation_type: type,
        probability: probability,
        predicted_timeframe: 'Next 7-14 days',
        risk_level: mostCommonSeverity === 'critical' ? 'critical' : mostCommonSeverity === 'high' ? 'high' : 'medium',
        contributing_factors: [
          `Historical frequency: ${data?.count} occurrences`,
          `Most common severity: ${mostCommonSeverity}`,
          data?.locations?.length > 0 ? `Affected locations: ${[...new Set(data.locations)]?.slice(0, 3)?.join(', ')}` : 'Multiple locations'
        ],
        affected_locations: [...new Set(data.locations)]?.slice(0, 5)
      };
    });

  // Generate interventions
  const proactive_interventions = predictions?.slice(0, 3)?.map(pred => ({
    action: `Review and address ${pred?.violation_type} compliance procedures`,
    priority: pred?.risk_level === 'critical' ? 'urgent' : pred?.risk_level === 'high' ? 'high' : 'medium',
    target_area: pred?.affected_locations?.[0] || 'All locations',
    estimated_impact: `Reduce ${pred?.violation_type} violations by monitoring patterns`
  }));

  // Identify high-risk periods
  const peakHour = Object.entries(timePatterns?.hourly)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0];
  const peakDay = Object.entries(timePatterns?.daily)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0];
  
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  const high_risk_periods = [
    {
      period: `${dayNames?.[peakDay]} around ${peakHour}:00`,
      risk_factors: ['Historical peak violation time', 'Increased operational activity']
    }
  ];

  return {
    predictions,
    proactive_interventions,
    high_risk_periods,
    confidence_score: 0.65,
    analysis_summary: `Historical pattern analysis identified ${predictions?.length} potential violation types based on ${complianceHistory?.length} past alerts. This fallback model uses statistical patterns when AI analysis is unavailable.`,
    fallback_mode: true
  };
}

/**
 * Historical fallback model for pest pressure predictions
 * Uses environmental correlation analysis when OpenAI API is unavailable
 */
function generateHistoricalPestPrediction(historicalData) {
  const pestPatterns = historicalData?.pestPatterns || [];
  const environmental = historicalData?.environmental || [];
  
  if (pestPatterns?.length === 0) {
    return {
      predictions: [],
      preventive_treatments: [],
      environmental_monitoring: [],
      confidence_score: 0.3,
      analysis_summary: 'Insufficient historical pest data for prediction. Using fallback model with limited accuracy.',
      fallback_mode: true
    };
  }

  // Analyze pest outbreak patterns
  const pestFrequency = {};
  const locationRisk = {};
  
  pestPatterns?.forEach(pattern => {
    const pest = pattern?.pest_name || 'unknown';
    const location = pattern?.location || 'unknown';
    const severity = pattern?.pest_severity || 'medium';
    
    if (!pestFrequency?.[pest]) {
      pestFrequency[pest] = { count: 0, severities: [], locations: [], avgTemp: [], avgHumidity: [] };
    }
    pestFrequency[pest].count++;
    pestFrequency?.[pest]?.severities?.push(severity);
    pestFrequency?.[pest]?.locations?.push(location);
    if (pattern?.temperature_avg) pestFrequency?.[pest]?.avgTemp?.push(pattern?.temperature_avg);
    if (pattern?.humidity_avg) pestFrequency?.[pest]?.avgHumidity?.push(pattern?.humidity_avg);
    
    locationRisk[location] = (locationRisk?.[location] || 0) + 1;
  });

  // Generate predictions
  const predictions = Object.entries(pestFrequency)?.sort((a, b) => b?.[1]?.count - a?.[1]?.count)?.slice(0, 5)?.map(([pest, data]) => {
      const probability = Math.min(data?.count / pestPatterns?.length * 1.2, 0.95);
      const avgSeverity = data?.severities?.filter(s => s === 'high' || s === 'critical')?.length / data?.severities?.length;
      const riskLevel = avgSeverity > 0.5 ? 'high' : avgSeverity > 0.25 ? 'medium' : 'low';
      
      const avgTemp = data?.avgTemp?.length > 0 
        ? (data?.avgTemp?.reduce((a, b) => a + b, 0) / data?.avgTemp?.length)?.toFixed(1)
        : 'N/A';
      const avgHumidity = data?.avgHumidity?.length > 0
        ? (data?.avgHumidity?.reduce((a, b) => a + b, 0) / data?.avgHumidity?.length)?.toFixed(1)
        : 'N/A';
      
      return {
        pest_type: pest,
        pressure_level: Math.min(data?.count / 10, 5),
        probability: probability,
        predicted_timeframe: 'Next 7-14 days',
        risk_level: riskLevel,
        environmental_triggers: [
          avgTemp !== 'N/A' ? `Optimal temperature: ${avgTemp}°F` : 'Temperature data unavailable',
          avgHumidity !== 'N/A' ? `Optimal humidity: ${avgHumidity}%` : 'Humidity data unavailable',
          'Historical outbreak conditions'
        ],
        affected_locations: [...new Set(data.locations)]?.slice(0, 5),
        growth_stages_at_risk: ['Vegetative', 'Flowering']
      };
    });

  // Generate preventive treatments
  const preventive_treatments = predictions?.slice(0, 3)?.map(pred => ({
    treatment: `Preventive IPM protocol for ${pred?.pest_type}`,
    target_pest: pred?.pest_type,
    recommended_timing: pred?.risk_level === 'high' ? 'Within 48 hours' : 'Within 7 days',
    priority: pred?.risk_level === 'high' ? 'urgent' : pred?.risk_level === 'medium' ? 'high' : 'medium',
    expected_effectiveness: 75
  }));

  // Environmental monitoring recommendations
  const recentReadings = environmental?.slice(0, 50);
  const sensorTypes = [...new Set(recentReadings.map(r => r?.sensor_type))]?.slice(0, 3);
  
  const environmental_monitoring = sensorTypes?.map(sensor => ({
    parameter: sensor || 'Environmental sensor',
    current_value: 'Monitor continuously',
    optimal_range: 'Based on pest thresholds',
    risk_assessment: 'Track for correlation with pest outbreaks'
  }));

  return {
    predictions,
    preventive_treatments,
    environmental_monitoring,
    confidence_score: 0.68,
    analysis_summary: `Historical pattern analysis identified ${predictions?.length} pest types with outbreak potential based on ${pestPatterns?.length} past occurrences. This fallback model uses statistical correlation when AI analysis is unavailable.`,
    fallback_mode: true
  };
}

export const predictiveAnalyticsService = {
  /**
   * Fetch historical data for predictive analysis
   */
  async fetchHistoricalData(companyId, dateRange = 90) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - dateRange);

      // Fetch treatment analytics with effectiveness ratings
      const { data: treatmentData, error: treatmentError } = await supabase
        ?.from('treatment_analytics')
        ?.select('*, products(name, category, active_ingredients)')
        ?.eq('company_id', companyId)
        ?.gte('created_at', startDate?.toISOString())
        ?.order('treatment_date', { ascending: false })
        ?.limit(500);

      if (treatmentError) throw treatmentError;

      // Fetch environmental readings
      const { data: environmentalData, error: envError } = await supabase
        ?.from('environmental_readings')
        ?.select('*, device:environmental_devices(device_name, location_zone)')
        ?.gte('reading_timestamp', startDate?.toISOString())
        ?.order('reading_timestamp', { ascending: false })
        ?.limit(1000);

      if (envError) throw envError;

      // Fetch pest outbreak patterns
      const { data: pestPatterns, error: pestError } = await supabase
        ?.from('pest_outbreak_patterns')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.gte('outbreak_date', startDate?.toISOString())
        ?.order('outbreak_date', { ascending: false });

      if (pestError) throw pestError;

      // Fetch compliance alerts history
      const { data: complianceHistory, error: complianceError } = await supabase
        ?.from('compliance_alerts')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.gte('created_at', startDate?.toISOString())
        ?.order('created_at', { ascending: false });

      if (complianceError) throw complianceError;

      return {
        treatments: treatmentData || [],
        environmental: environmentalData || [],
        pestPatterns: pestPatterns || [],
        complianceHistory: complianceHistory || []
      };
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw new Error('Failed to fetch historical data for predictive analysis');
    }
  },

  /**
   * Predict compliance violations using OpenAI GPT-5
   */
  async predictComplianceViolations(companyId) {
    // Check cache first
    const cacheKey = `compliance_${companyId}`;
    const cached = getCachedPrediction(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const historicalData = await this.fetchHistoricalData(companyId);

      // Analyze compliance patterns
      const compliancePatterns = historicalData?.complianceHistory?.reduce((acc, alert) => {
        const alertType = alert?.alert_type || 'unknown';
        const severity = alert?.severity || 'medium';
        const hour = new Date(alert?.created_at)?.getHours();
        const dayOfWeek = new Date(alert?.created_at)?.getDay();

        if (!acc?.[alertType]) {
          acc[alertType] = { count: 0, severities: {}, timePatterns: {} };
        }
        acc[alertType].count++;
        acc[alertType].severities[severity] = (acc?.[alertType]?.severities?.[severity] || 0) + 1;
        acc[alertType].timePatterns[`day_${dayOfWeek}_hour_${hour}`] = 
          (acc?.[alertType]?.timePatterns?.[`day_${dayOfWeek}_hour_${hour}`] || 0) + 1;

        return acc;
      }, {});

      // Analyze treatment compliance
      const treatmentCompliance = historicalData?.treatments?.map(t => ({
        date: t?.treatment_date,
        effectiveness: t?.effectiveness_rating,
        product: t?.products?.name,
        category: t?.products?.category,
        location: t?.field_location
      }));

      const prompt = `Analyze the following agricultural compliance data and predict potential violations:

Compliance Alert Patterns (Last 90 days):
${JSON.stringify(compliancePatterns, null, 2)}

Treatment History Summary:
- Total Treatments: ${treatmentCompliance?.length}
- Average Effectiveness: ${(treatmentCompliance?.reduce((sum, t) => sum + (t?.effectiveness || 0), 0) / treatmentCompliance?.length)?.toFixed(2)}%
- Locations: ${[...new Set(treatmentCompliance?.map(t => t?.location))]?.join(', ')}

Environmental Conditions:
- Total Readings: ${historicalData?.environmental?.length}
- Sensor Types: ${[...new Set(historicalData?.environmental?.map(r => r?.sensor_type))]?.join(', ')}

Pest Outbreak History:
- Total Outbreaks: ${historicalData?.pestPatterns?.length}
- Pest Types: ${[...new Set(historicalData?.pestPatterns?.map(p => p?.pest_name))]?.join(', ')}

Based on these patterns, predict:
1. Likely compliance violations in the next 7-14 days
2. Risk factors and contributing conditions
3. Recommended proactive interventions
4. Specific areas/locations requiring attention
5. Timing predictions for when violations are most likely`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are an expert agricultural compliance analyst specializing in predictive analytics for IPM operations. Analyze historical patterns to predict future compliance violations with high accuracy. Focus on actionable insights that enable proactive supervisor interventions.'
          },
          { role: 'user', content: prompt }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'compliance_violation_predictions',
            schema: {
              type: 'object',
              properties: {
                predictions: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      violation_type: { type: 'string' },
                      probability: { type: 'number', minimum: 0, maximum: 1 },
                      predicted_timeframe: { type: 'string' },
                      risk_level: { type: 'string', enum: ['low', 'medium', 'high', 'critical'] },
                      contributing_factors: { type: 'array', items: { type: 'string' } },
                      affected_locations: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['violation_type', 'probability', 'predicted_timeframe', 'risk_level']
                  }
                },
                proactive_interventions: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      action: { type: 'string' },
                      priority: { type: 'string', enum: ['low', 'medium', 'high', 'urgent'] },
                      target_area: { type: 'string' },
                      estimated_impact: { type: 'string' }
                    },
                    required: ['action', 'priority']
                  }
                },
                high_risk_periods: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      period: { type: 'string' },
                      risk_factors: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['period', 'risk_factors']
                  }
                },
                confidence_score: {
                  type: 'number',
                  minimum: 0,
                  maximum: 1
                },
                analysis_summary: { type: 'string' }
              },
              required: ['predictions', 'proactive_interventions', 'high_risk_periods', 'confidence_score', 'analysis_summary'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'high'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error predicting compliance violations:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Predict compliance violations with retry logic and fallback
   */
  async predictComplianceViolationsWithRetry(companyId) {
    const cacheKey = `compliance_${companyId}`;
    const cached = getCachedPrediction(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const result = await retryWithBackoff(async () => {
        return await this.predictComplianceViolations(companyId);
      }, 3, 1000);
      
      setCachedPrediction(cacheKey, result);
      return result;
    } catch (error) {
      console.warn('OpenAI API failed after retries, falling back to historical model:', error?.message);
      
      // Fallback to historical prediction model
      try {
        const historicalData = await this.fetchHistoricalData(companyId);
        const fallbackResult = generateHistoricalCompliancePrediction(historicalData);
        
        // Cache fallback result with shorter duration
        predictionCache?.set(cacheKey, {
          data: fallbackResult,
          timestamp: Date.now()
        });
        
        return fallbackResult;
      } catch (fallbackError) {
        console.error('Fallback prediction also failed:', fallbackError);
        throw new Error('Unable to generate compliance predictions. Please try again later.');
      }
    }
  },

  /**
   * Predict pest pressure spikes using OpenAI GPT-5
   */
  async predictPestPressureSpikes(companyId) {
    try {
      const historicalData = await this.fetchHistoricalData(companyId);

      // Analyze environmental conditions during pest outbreaks
      const pestEnvironmentalCorrelations = historicalData?.pestPatterns?.map(pattern => {
        const outbreakDate = new Date(pattern?.outbreak_date);
        
        // Find environmental readings around outbreak time (±24 hours)
        const relevantReadings = historicalData?.environmental?.filter(reading => {
          const readingDate = new Date(reading?.reading_timestamp);
          const timeDiff = Math.abs(readingDate - outbreakDate) / (1000 * 60 * 60);
          return timeDiff <= 24;
        });

        return {
          pest: pattern?.pest_name,
          severity: pattern?.pest_severity,
          temperature: pattern?.temperature_avg,
          humidity: pattern?.humidity_avg,
          vpd: pattern?.vpd_avg,
          location: pattern?.location,
          growthStage: pattern?.growth_stage,
          treatmentEffectiveness: pattern?.treatment_effectiveness,
          daysToControl: pattern?.days_to_control,
          environmentalReadings: relevantReadings?.length
        };
      });

      // Current environmental conditions
      const recentReadings = historicalData?.environmental?.slice(0, 100);
      const currentConditions = recentReadings?.reduce((acc, reading) => {
        const sensorType = reading?.sensor_type;
        if (!acc?.[sensorType]) {
          acc[sensorType] = { values: [], avg: 0, min: 0, max: 0 };
        }
        acc?.[sensorType]?.values?.push(reading?.value);
        return acc;
      }, {});

      // Calculate averages
      Object.keys(currentConditions)?.forEach(sensor => {
        const values = currentConditions?.[sensor]?.values;
        currentConditions[sensor].avg = values?.reduce((a, b) => a + b, 0) / values?.length;
        currentConditions[sensor].min = Math.min(...values);
        currentConditions[sensor].max = Math.max(...values);
      });

      const prompt = `Analyze pest outbreak patterns and environmental conditions to predict pest pressure spikes:

Historical Pest Outbreaks (Last 90 days):
${JSON.stringify(pestEnvironmentalCorrelations?.slice(0, 20), null, 2)}

Current Environmental Conditions:
${JSON.stringify(currentConditions, null, 2)}

Treatment Effectiveness History:
- Total Treatments: ${historicalData?.treatments?.length}
- Average Effectiveness: ${(historicalData?.treatments?.reduce((sum, t) => sum + (t?.effectiveness_rating || 0), 0) / historicalData?.treatments?.length)?.toFixed(2)}%
- Most Used Products: ${[...new Set(historicalData?.treatments?.map(t => t?.products?.name)?.filter(Boolean))]?.slice(0, 5)?.join(', ')}

Based on environmental correlations and historical patterns, predict:
1. Pest pressure spikes in the next 7-14 days
2. Environmental triggers and optimal conditions for outbreaks
3. Specific pest types most likely to spike
4. Locations at highest risk
5. Recommended preventive treatments and timing`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are an expert entomologist and IPM specialist with deep knowledge of pest behavior, environmental triggers, and predictive modeling. Analyze environmental conditions and historical outbreak patterns to predict pest pressure spikes with high accuracy. Provide actionable recommendations for proactive pest management.'
          },
          { role: 'user', content: prompt }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'pest_pressure_predictions',
            schema: {
              type: 'object',
              properties: {
                predictions: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      pest_type: { type: 'string' },
                      pressure_level: { type: 'number', minimum: 0, maximum: 5 },
                      probability: { type: 'number', minimum: 0, maximum: 1 },
                      predicted_timeframe: { type: 'string' },
                      risk_level: { type: 'string', enum: ['low', 'medium', 'high', 'critical'] },
                      environmental_triggers: { type: 'array', items: { type: 'string' } },
                      affected_locations: { type: 'array', items: { type: 'string' } },
                      growth_stages_at_risk: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['pest_type', 'pressure_level', 'probability', 'predicted_timeframe', 'risk_level']
                  }
                },
                preventive_treatments: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      treatment: { type: 'string' },
                      target_pest: { type: 'string' },
                      recommended_timing: { type: 'string' },
                      priority: { type: 'string', enum: ['low', 'medium', 'high', 'urgent'] },
                      expected_effectiveness: { type: 'number', minimum: 0, maximum: 100 }
                    },
                    required: ['treatment', 'target_pest', 'recommended_timing', 'priority']
                  }
                },
                environmental_monitoring: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      parameter: { type: 'string' },
                      current_value: { type: 'string' },
                      optimal_range: { type: 'string' },
                      risk_assessment: { type: 'string' }
                    },
                    required: ['parameter', 'risk_assessment']
                  }
                },
                confidence_score: {
                  type: 'number',
                  minimum: 0,
                  maximum: 1
                },
                analysis_summary: { type: 'string' }
              },
              required: ['predictions', 'preventive_treatments', 'environmental_monitoring', 'confidence_score', 'analysis_summary'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'high'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error predicting pest pressure spikes:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Predict pest pressure spikes with retry logic and fallback
   */
  async predictPestPressureSpikesWithRetry(companyId) {
    const cacheKey = `pest_${companyId}`;
    const cached = getCachedPrediction(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const result = await retryWithBackoff(async () => {
        return await this.predictPestPressureSpikes(companyId);
      }, 3, 1000);
      
      setCachedPrediction(cacheKey, result);
      return result;
    } catch (error) {
      console.warn('OpenAI API failed after retries, falling back to historical model:', error?.message);
      
      // Fallback to historical prediction model
      try {
        const historicalData = await this.fetchHistoricalData(companyId);
        const fallbackResult = generateHistoricalPestPrediction(historicalData);
        
        // Cache fallback result with shorter duration
        predictionCache?.set(cacheKey, {
          data: fallbackResult,
          timestamp: Date.now()
        });
        
        return fallbackResult;
      } catch (fallbackError) {
        console.error('Fallback prediction also failed:', fallbackError);
        throw new Error('Unable to generate pest pressure predictions. Please try again later.');
      }
    }
  },

  /**
   * Get combined predictive insights
   */
  async getCombinedPredictions(companyId) {
    try {
      // Sequential calls to avoid rate limits (instead of Promise.all)
      // This reduces the chance of hitting rate limits
      const compliancePredictions = await this.predictComplianceViolationsWithRetry(companyId);
      
      // Add small delay between calls
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const pestPredictions = await this.predictPestPressureSpikesWithRetry(companyId);

      return {
        compliance: compliancePredictions,
        pestPressure: pestPredictions,
        generatedAt: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error getting combined predictions:', error);
      throw error;
    }
  }
};
